import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import axiosInstance from '../../axiosInstance';
import StarRating from './StarRating';
import BarcodeScanner from '../BarcodeScanner';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const DNF_FIELDS = {
  book:           [{ key: 'dnfPage', label: 'Page number', type: 'number' }, { key: 'dnfChapter', label: 'Chapter / section', type: 'text' }],
  game:           [{ key: 'dnfChapter', label: 'Chapter / act (e.g. act 2 / chapter 6)', type: 'text' }],
  movie:          [{ key: 'dnfTimestamp', label: 'Time into film (HH:MM)', type: 'text' }],
  manga:          [{ key: 'dnfVolume', label: 'Volume', type: 'number' }, { key: 'dnfChapter', label: 'Chapter', type: 'number' }],
  comic:          [{ key: 'dnfIssue', label: 'Issue', type: 'number' }, { key: 'dnfPage', label: 'Page', type: 'number' }],
  music:          [{ key: 'dnfTrack', label: 'Track number', type: 'number' }],
  console:        [],
  lego:           [],
  'board-game':   [],
  'trading-card': [],
};

/**
 * Generic edit dialog component for collection/wishlist items
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Function to handle dialog close
 * @param {Object} props.item - Item being edited
 * @param {function} props.onSave - Function to handle saving edited item
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist item
 * @param {Array} props.fields - Custom fields configuration
 * @returns {JSX.Element} Edit dialog component
 */
const EditDialog = ({
  open,
  onClose,
  item,
  onSave,
  itemType = 'game',
  isWishlist = false,
  fields = []
}) => {
  const { featureFlags } = useFeatureFlags();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [importUrlByField, setImportUrlByField] = useState({});
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [barcodeLookupLoading, setBarcodeLookupLoading] = useState(false);
  const [barcodeLookupError, setBarcodeLookupError] = useState('');
  const [barcodeLastDetected, setBarcodeLastDetected] = useState('');
  const [barcodeDialogPhase, setBarcodeDialogPhase] = useState('scanning');
  const [barcodeLookupResult, setBarcodeLookupResult] = useState(null);
  const [barcodeLogs, setBarcodeLogs] = useState([]);
  const barcodeLogKey = 'barcodeScannerLogs:editDialog';
  const [igdbLookupLoading, setIgdbLookupLoading] = useState(false);
  const [igdbLookupStatus, setIgdbLookupStatus] = useState(null); // null | 'ok' | 'error'
  const [igdbLookupMessage, setIgdbLookupMessage] = useState('');

  const isImageUrlField = (fieldName) => {
    const n = String(fieldName || '').toLowerCase();

    if (!n) return false;

    if (n === 'imageurl' || n === 'platformlogo' || n === 'set_img_url' || n === 'setimgurl') {
      return true;
    }

    const looksLikeUrl = n.includes('url');
    const looksLikeImage = n.includes('image') || n.includes('img') || n.includes('poster') || n.includes('cover') || n.includes('artwork');
    return looksLikeUrl && looksLikeImage;
  };

  // Reset form data when item changes
  useEffect(() => {
    if (item) {
      // Create a copy of the item for processing
      const processedItem = { ...item };
      
      // Special handling for different item types
      if (itemType === 'console' && processedItem.platformLogo && !processedItem.imageUrl) {
        // For console items, ensure platformLogo is copied to imageUrl if imageUrl is empty
        processedItem.imageUrl = processedItem.platformLogo;
      } else if (itemType === 'lego') {
        // For Lego items, ensure all required fields are properly set
        
        // Handle set number field variations
        if (processedItem.setNumber && !processedItem.setNum) {
          processedItem.setNum = processedItem.setNumber;
        } else if (processedItem.setNum && !processedItem.setNumber) {
          processedItem.setNumber = processedItem.setNum;
        }
        
        // Handle image URL field variations
        if (processedItem.imageUrl && !processedItem.set_img_url) {
          processedItem.set_img_url = processedItem.imageUrl;
        } else if (processedItem.set_img_url && !processedItem.imageUrl) {
          processedItem.imageUrl = processedItem.set_img_url;
        }
        
        // Handle theme field variations
        if (processedItem.theme && !processedItem.themeId) {
          processedItem.themeId = processedItem.theme;
        } else if (processedItem.themeId && !processedItem.theme) {
          processedItem.theme = processedItem.themeId;
        }
      }
      
      setFormData(processedItem);
      setErrors({});
      setIgdbLookupStatus(null);
      setIgdbLookupMessage('');
    }
  }, [item, itemType]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleBarcodeDetected = async (code) => {
    try {
      const trimmed = String(code || '').trim();
      if (!trimmed) return;

      setBarcodeLastDetected(trimmed);

      setBarcodeLookupResult(null);
      setBarcodeDialogPhase('looking_up');

      setBarcodeLookupLoading(true);
      setBarcodeLookupError('');

      setBarcodeLogs((prev) => {
        const entry = { t: new Date().toISOString(), message: 'lookup_start', extra: { code: trimmed } };
        const next = [...(Array.isArray(prev) ? prev : []), entry].slice(-500);
        try {
          localStorage.setItem(barcodeLogKey, JSON.stringify(next));
        } catch (e) {
          // no-op
        }
        return next;
      });

      const base = typeof axiosInstance.getBaseServerUrl === 'function' ? axiosInstance.getBaseServerUrl() : (axiosInstance.defaults.baseURL || '');
      const url = base
        ? `${base}/api/barcodes/${encodeURIComponent(trimmed)}?itemType=game`
        : `/barcodes/${encodeURIComponent(trimmed)}?itemType=game`;
      const response = await axiosInstance.get(url);
      const result = response?.data?.result;
      const item = result?.item;

      setBarcodeLogs((prev) => {
        const entry = { t: new Date().toISOString(), message: 'lookup_response', extra: { found: Boolean(result?.found), title: item?.title || null } };
        const next = [...(Array.isArray(prev) ? prev : []), entry].slice(-500);
        try {
          localStorage.setItem(barcodeLogKey, JSON.stringify(next));
        } catch (e) {
          // no-op
        }
        return next;
      });

      if (!result) {
        setBarcodeLookupError('No result returned for that barcode');
        setBarcodeDialogPhase('not_found');
        return;
      }

      if (!result?.found) {
        setBarcodeLookupError('No product found for that barcode');
        setBarcodeDialogPhase('not_found');

        setBarcodeLogs((prev) => {
          const entry = { t: new Date().toISOString(), message: 'lookup_not_found', extra: { code: trimmed } };
          const next = [...(Array.isArray(prev) ? prev : []), entry].slice(-500);
          try {
            localStorage.setItem(barcodeLogKey, JSON.stringify(next));
          } catch (e) {
            // no-op
          }
          return next;
        });

        return;
      }

      const cleanedTitle = String(item?.title || '').trim();
      setBarcodeLookupResult({
        code: trimmed,
        cleanedTitle,
        rawItem: item || null
      });
      setBarcodeDialogPhase('found');
    } catch (err) {
      console.error('Barcode lookup failed:', err);
      setBarcodeLookupError(err?.response?.data?.error || 'Barcode lookup failed');
      setBarcodeDialogPhase('not_found');

      setBarcodeLogs((prev) => {
        const entry = {
          t: new Date().toISOString(),
          message: 'lookup_error',
          extra: {
            error: err?.message || String(err),
            upstreamStatus: err?.response?.status || null
          }
        };
        const next = [...(Array.isArray(prev) ? prev : []), entry].slice(-500);
        try {
          localStorage.setItem(barcodeLogKey, JSON.stringify(next));
        } catch (e) {
          // no-op
        }
        return next;
      });
    } finally {
      setBarcodeLookupLoading(false);
    }
  };

  const handleBarcodeDialogClose = () => {
    setBarcodeDialogOpen(false);
    setBarcodeLookupLoading(false);
    setBarcodeLookupError('');
    setBarcodeLastDetected('');
    setBarcodeLookupResult(null);
    setBarcodeDialogPhase('scanning');
  };

  const handleBarcodeUseResult = () => {
    const trimmed = String(barcodeLookupResult?.code || '').trim();
    const title = String(barcodeLookupResult?.cleanedTitle || '').trim();
    const rawItem = barcodeLookupResult?.rawItem || null;
    if (!trimmed) return;

    // Prefill fields (only if empty) so we don't overwrite user-entered values
    setFormData(prev => ({
      ...prev,
      barcode: prev.barcode || trimmed,
      name: prev.name || title || prev.name,
      imageUrl: prev.imageUrl || rawItem?.image || prev.imageUrl
    }));

    handleBarcodeDialogClose();
  };

  const setConsoleImageFields = (fieldName, url) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: url
    }));
  };

  const uploadImageFile = async (fieldName, file) => {
    try {
      if (!file) return;
      setUploadingField(fieldName);
      const data = new FormData();
      data.append('file', file);

      const response = await axiosInstance.post('/images/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response?.data?.url) {
        setConsoleImageFields(fieldName, response.data.url);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setErrors(prev => ({
        ...prev,
        [fieldName]: 'Image upload failed'
      }));
    } finally {
      setUploadingField(null);
    }
  };

  const importImageFromUrl = async (fieldName) => {
    try {
      const url = (importUrlByField[fieldName] || '').trim();
      if (!url) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: 'Enter a URL to import'
        }));
        return;
      }

      setUploadingField(fieldName);
      const response = await axiosInstance.post('/images/import', { url });
      if (response?.data?.url) {
        setConsoleImageFields(fieldName, response.data.url);
        setImportUrlByField(prev => ({ ...prev, [fieldName]: '' }));
      }
    } catch (err) {
      console.error('Image import failed:', err);
      setErrors(prev => ({
        ...prev,
        [fieldName]: 'Image import failed'
      }));
    } finally {
      setUploadingField(null);
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Check required fields based on item type
    if (itemType === 'game') {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      }
      if (!formData.platform?.trim()) {
        newErrors.platform = 'Platform is required';
        isValid = false;
      }
    } else if (itemType === 'console') {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      }
    } else if (itemType === 'lego') {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      }
      // Check for set number in any of its variations
      if (!formData.setNum?.trim() && !formData.setNumber?.trim() && !formData.set_num?.trim()) {
        newErrors.setNum = 'Set number is required';
        isValid = false;
      }
    } else if (itemType === 'movie') {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      }
      if (!formData.format?.trim()) {
        newErrors.format = 'Format is required';
        isValid = false;
      }
    } else if (itemType === 'music') {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
        isValid = false;
      }
      if (!formData.artist?.trim()) {
        newErrors.artist = 'Artist is required';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of the form data for processing
    const processedData = { ...formData };
    
    // Special handling for console items
    if (itemType === 'console') {
      // If imageUrl is empty but platformLogo has a value, copy platformLogo to imageUrl
      if (!processedData.imageUrl && processedData.platformLogo) {
        processedData.imageUrl = processedData.platformLogo;
      }
    }
    
    // Special handling for Lego items
    if (itemType === 'lego') {
      // Ensure both set number fields are populated
      if (processedData.setNum && !processedData.setNumber) {
        processedData.setNumber = processedData.setNum;
      } else if (processedData.setNumber && !processedData.setNum) {
        processedData.setNum = processedData.setNumber;
      }
      
      // Ensure both image URL fields are populated
      if (processedData.imageUrl && !processedData.set_img_url) {
        processedData.set_img_url = processedData.imageUrl;
      } else if (processedData.set_img_url && !processedData.imageUrl) {
        processedData.imageUrl = processedData.set_img_url;
      }
      
      // Ensure both theme and themeId fields are populated
      if (processedData.theme && !processedData.themeId) {
        processedData.themeId = processedData.theme;
      } else if (processedData.themeId && !processedData.theme) {
        processedData.theme = processedData.themeId;
      }
    }
    
    // Special handling for movie items
    if (itemType === 'movie') {
      // Ensure watched is a boolean
      if (processedData.watched !== undefined) {
        processedData.watched = Boolean(processedData.watched);
      }
      
      // Ensure priority is a string
      if (processedData.priority && typeof processedData.priority === 'object') {
        processedData.priority = processedData.priority.value || processedData.priority;
      }
    }
    
    // Call onSave with processed form data
    onSave(processedData);
  };

  // Default fields based on item type
  const getDefaultFields = () => {
    const defaultFields = [
      {
        name: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        gridProps: { xs: 12 }
      },
      {
        name: 'imageUrl',
        label: 'Image URL',
        type: 'text',
        required: false,
        gridProps: { xs: 12 }
      },
      {
        name: 'rating',
        label: 'Rating',
        type: 'rating',
        required: false,
        gridProps: { xs: 12 }
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'text',
        multiline: true,
        required: false,
        gridProps: { xs: 12 }
      }
    ];

    if (itemType === 'game') {
      return [
        ...defaultFields,
        {
          name: 'platform',
          label: 'Platform',
          type: 'text',
          required: true,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'select',
          options: ['Low', 'Medium', 'High'],
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'played',
          label: 'Played',
          type: 'checkbox',
          required: false,
          gridProps: { xs: 6 }
        },
        {
          name: 'completed',
          label: 'Completed',
          type: 'checkbox',
          required: false,
          gridProps: { xs: 6 }
        }
      ];
    } else if (itemType === 'console') {
      return [
        ...defaultFields.filter(field => field.name !== 'imageUrl'),
        {
          name: 'generation',
          label: 'Generation',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'condition',
          label: 'Condition',
          type: 'select',
          options: [
            'Mint (M)',
            'Near Mint (NM)',
            'Excellent (EX)',
            'Very Good (VG)',
            'Good (G)',
            'Fair (F)',
            'Poor (P)',
            'For Parts or Not Working'
          ],
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'alternative_name',
          label: 'Alternative Name',
          type: 'text',
          required: false,
          gridProps: { xs: 12 }
        },
        {
          name: 'imageUrl',
          label: 'Console Image (used on shelf)',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'platformLogo',
          label: 'Platform Logo',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'manufacturer',
          label: 'Manufacturer',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'model',
          label: 'Model',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'version',
          label: 'Version',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'year',
          label: 'Year',
          type: 'number',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        }
      ];
    } else if (itemType === 'lego') {
      return [
        ...defaultFields,
        {
          name: 'setNum',
          label: 'Set Number',
          type: 'text',
          required: true,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'themeId',
          label: 'Theme ID',
          type: 'text',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'set_img_url',
          label: 'Image URL',
          type: 'text',
          required: false,
          gridProps: { xs: 12 }
        },
        {
          name: 'year',
          label: 'Year',
          type: 'number',
          required: false,
          gridProps: { xs: 12, sm: 6 }
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'select',
          options: ['Low', 'Medium', 'High'],
          required: false,
          gridProps: { xs: 12, sm: 6 }
        }
      ];
    }

    return defaultFields;
  };

  // Combine default fields with custom fields
  const combinedFields = fields.length > 0 ? fields : getDefaultFields();

  const normalizeAndOrderFields = (inputFields) => {
    const list = Array.isArray(inputFields) ? [...inputFields] : [];

    const hasPlatformLogo = list.some(f => f?.name === 'platformLogo');
    if (!hasPlatformLogo && itemType === 'console') {
      list.push({
        name: 'platformLogo',
        label: 'Platform Logo',
        type: 'text',
        required: false,
        gridProps: { xs: 12 }
      });
    }

    // Inject sold fields for collection items (not wishlists, not if already present)
    if (!isWishlist && !list.some(f => f?.name === 'isSold')) {
      list.push(
        { name: 'isSold', label: 'Mark as Sold', type: 'checkbox', required: false, gridProps: { xs: 12 } },
        { name: 'soldPrice', label: 'Sold For (£)', type: 'number', showWhen: 'isSold', required: false, gridProps: { xs: 6 }, inputProps: { min: 0, step: 0.01 } },
        { name: 'soldDate', label: 'Date Sold', type: 'date', showWhen: 'isSold', required: false, gridProps: { xs: 6 } }
      );
    }

    const notesIdx = list.findIndex(f => f?.name === 'notes');
    const notesField = notesIdx >= 0 ? list.splice(notesIdx, 1)[0] : null;

    const imageFields = [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (isImageUrlField(list[i]?.name)) {
        imageFields.unshift(list.splice(i, 1)[0]);
      }
    }

    if (notesField) {
      list.push(notesField);
    }

    for (const f of imageFields) {
      list.push(f);
    }

    return list;
  };

  const orderedFields = normalizeAndOrderFields(combinedFields);

  // Render form fields based on type
  const renderField = (field) => {
    const { name, label, type, required, options, helperText, multiline, rows, gridProps, showWhen, ...rest } = field;

    // Conditionally render based on another field's value
    if (showWhen) {
      const hidden = typeof showWhen === 'function' ? !showWhen(formData) : !formData[showWhen];
      if (hidden) return null;
    }

    if (isImageUrlField(name)) {
      const isBusy = uploadingField === name;

      return (
        <Grid item {...gridProps} key={name}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              fullWidth
              id={name}
              name={name}
              label={label}
              value={formData[name] || ''}
              onChange={handleChange}
              required={required}
              error={!!errors[name]}
              helperText={errors[name] || helperText || ''}
              type="text"
              {...rest}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="outlined" component="label" disabled={isBusy}>
                Take Photo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    uploadImageFile(name, file);
                  }}
                />
              </Button>

              <Button variant="outlined" component="label" disabled={isBusy}>
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*,.heic,.heif"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    uploadImageFile(name, file);
                  }}
                />
              </Button>

              <TextField
                size="small"
                label="Import from URL"
                value={importUrlByField[name] || ''}
                onChange={(e) => setImportUrlByField(prev => ({ ...prev, [name]: e.target.value }))}
                sx={{ minWidth: 260, flexGrow: 1 }}
                disabled={isBusy}
              />

              <Button variant="contained" onClick={() => importImageFromUrl(name)} disabled={isBusy}>
                Import
              </Button>

              {isBusy && <CircularProgress size={22} />}
            </Box>
          </Box>
        </Grid>
      );
    }
    
    switch (type) {
      case 'select':
        return (
          <Grid item {...gridProps} key={name}>
            <FormControl fullWidth error={!!errors[name]}>
              <InputLabel id={`${name}-label`}>{label}</InputLabel>
              <Select
                labelId={`${name}-label`}
                id={name}
                name={name}
                value={formData[name] || ''}
                label={label}
                onChange={handleChange}
                required={required}
                {...rest}
              >
                {options.map((option) => {
                  // Handle both string options and object options with value/label properties
                  if (typeof option === 'object' && option !== null && 'value' in option && 'label' in option) {
                    return (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    );
                  } else {
                    return (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    );
                  }
                })}
              </Select>
              {errors[name] && (
                <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '3px' }}>
                  {errors[name]}
                </div>
              )}
            </FormControl>
          </Grid>
        );
      
      case 'rating':
        return (
          <Grid item {...gridProps} key={name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">{label}:</Typography>
              <StarRating
                value={formData[name] || null}
                onChange={(newValue) => handleChange({ target: { name, value: newValue } })}
                size="large"
              />
              {formData[name] > 0 && (
                <Button
                  size="small"
                  onClick={() => handleChange({ target: { name, value: null } })}
                  sx={{ minWidth: 'auto', ml: 1 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        );

      case 'checkbox':
        return (
          <Grid item {...gridProps} key={name}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData[name]}
                  onChange={handleChange}
                  name={name}
                  color="primary"
                />
              }
              label={label}
            />
          </Grid>
        );
      
      case 'date': {
        // Normalise to YYYY-MM-DD for the HTML date input
        const rawDate = formData[name];
        const dateValue = rawDate
          ? new Date(rawDate).toISOString().split('T')[0]
          : '';
        return (
          <Grid item {...gridProps} key={name}>
            <TextField
              fullWidth
              id={name}
              name={name}
              label={label}
              value={dateValue}
              onChange={handleChange}
              required={required}
              error={!!errors[name]}
              helperText={errors[name] || helperText || ''}
              type="date"
              InputLabelProps={{ shrink: true }}
              {...rest}
            />
          </Grid>
        );
      }

      case 'igdb-lookup':
        return (
          <Grid item {...gridProps} key={name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                disabled={igdbLookupLoading}
                onClick={async () => {
                  setIgdbLookupLoading(true);
                  setIgdbLookupStatus(null);
                  setIgdbLookupMessage('');
                  try {
                    const resp = await axiosInstance.post(`games/${item._id}/igdb-lookup`);
                    const { matched, updates } = resp.data;
                    setFormData(prev => ({ ...prev, ...updates }));
                    setIgdbLookupStatus('ok');
                    setIgdbLookupMessage(`Matched "${matched}" — ${Object.keys(updates).join(', ')} updated`);
                  } catch (err) {
                    setIgdbLookupStatus('error');
                    setIgdbLookupMessage(err.response?.data?.error || 'Lookup failed');
                  } finally {
                    setIgdbLookupLoading(false);
                  }
                }}
              >
                {igdbLookupLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Re-lookup from IGDB
              </Button>
              {igdbLookupMessage && (
                <Typography variant="caption" color={igdbLookupStatus === 'ok' ? 'success.main' : 'error.main'}>
                  {igdbLookupMessage}
                </Typography>
              )}
            </Box>
          </Grid>
        );

      case 'text':
      case 'number':
      default:
        return (
          <Grid item {...gridProps} key={name}>
            <TextField
              fullWidth
              id={name}
              name={name}
              label={label}
              value={formData[name] || ''}
              onChange={handleChange}
              required={required}
              error={!!errors[name]}
              helperText={errors[name] || helperText || ''}
              type={type === 'number' ? 'number' : 'text'}
              multiline={multiline}
              rows={rows || 4}
              {...rest}
            />
          </Grid>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {item?._id ? 'Edit' : 'Add'} {(itemType || 'Item').charAt(0).toUpperCase() + (itemType || 'Item').slice(1)}
      </DialogTitle>
      <DialogContent>
        {itemType === 'game' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
            <Button variant="outlined" onClick={() => {
              setBarcodeLookupError('');
              setBarcodeLastDetected('');
              setBarcodeLookupResult(null);
              setBarcodeDialogPhase('scanning');

              try {
                const raw = localStorage.getItem(barcodeLogKey);
                const parsed = raw ? JSON.parse(raw) : [];
                setBarcodeLogs(Array.isArray(parsed) ? parsed : []);
              } catch (e) {
                setBarcodeLogs([]);
              }

              setBarcodeDialogOpen(true);
            }}>
              Scan Barcode
            </Button>
            {barcodeLookupLoading && <CircularProgress size={20} />}
            {barcodeLookupError && (
              <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
                {barcodeLookupError}
              </Box>
            )}
          </Box>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {orderedFields.map(renderField)}
        </Grid>

        {!isWishlist && featureFlags.enableBacklog === true && !['console', 'trading-card'].includes(itemType) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(128,128,128,0.2)' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.inBacklog}
                  onChange={(e) => setFormData(prev => ({ ...prev, inBacklog: e.target.checked }))}
                  color="secondary"
                />
              }
              label={<Typography variant="body2" fontWeight={600}>Add to Backlog</Typography>}
            />
          </Box>
        )}

        {!isWishlist && featureFlags.enableDNF === true && !['console', 'trading-card'].includes(itemType) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(128,128,128,0.2)' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.isDNF}
                  onChange={(e) => setFormData(prev => ({ ...prev, isDNF: e.target.checked }))}
                  color="warning"
                />
              }
              label={<Typography variant="body2" fontWeight={600}>Did Not Finish (DNF)</Typography>}
            />

            {formData.isDNF && (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Progress (%)"
                    type="number"
                    value={formData.dnfPercent ?? ''}
                    onChange={(e) => {
                      const v = e.target.value === '' ? null : Math.min(100, Math.max(0, Number(e.target.value)));
                      setFormData(prev => ({ ...prev, dnfPercent: v }));
                    }}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                {(DNF_FIELDS[itemType] || []).map(field => (
                  <Grid item xs={12} sm={4} key={field.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={field.label}
                      type={field.type}
                      value={formData[field.key] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === ''
                          ? null
                          : field.type === 'number' ? Number(e.target.value) : e.target.value;
                        setFormData(prev => ({ ...prev, [field.key]: v }));
                      }}
                      inputProps={field.type === 'number' ? { min: 0 } : { maxLength: 60 }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>

      <Dialog open={barcodeDialogOpen} onClose={handleBarcodeDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Scan Barcode</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', minHeight: 320 }}>
            <BarcodeScanner
              onDetected={handleBarcodeDetected}
              active={barcodeDialogPhase === 'scanning' || barcodeDialogPhase === 'looking_up'}
              logKey={barcodeLogKey}
              onLog={(entry) => setBarcodeLogs((prev) => [...(Array.isArray(prev) ? prev : []), entry].slice(-500))}
            />
          </Box>
          {barcodeLastDetected && (
            <Box sx={{ fontSize: '0.875rem', mt: 2 }}>
              Last detected: {barcodeLastDetected}
            </Box>
          )}
          {barcodeDialogPhase === 'looking_up' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Box sx={{ fontSize: '0.875rem' }}>Looking up barcode…</Box>
            </Box>
          )}
          {barcodeDialogPhase === 'found' && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Found</Box>
              <Box sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                {barcodeLookupResult?.cleanedTitle || ''}
              </Box>
            </Box>
          )}
          {barcodeDialogPhase === 'not_found' && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem', mt: 2 }}>
              {barcodeLookupError || 'No product found for that barcode'}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Scanner Logs</Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      const base = typeof axiosInstance.getBaseServerUrl === 'function' ? axiosInstance.getBaseServerUrl() : (axiosInstance.defaults.baseURL || '');
                      const url = base
                        ? `${base}/api/client-logs/barcode`
                        : '/client-logs/barcode';
                      await axiosInstance.post(url, {
                        source: 'edit_dialog',
                        logs: barcodeLogs,
                        context: {
                          itemType,
                          lastDetected: barcodeLastDetected || null,
                          userAgent: navigator.userAgent || null
                        }
                      });
                    } catch (e) {
                      // no-op
                    }
                  }}
                >
                  Upload
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      const text = JSON.stringify(barcodeLogs, null, 2);
                      await navigator.clipboard.writeText(text);
                    } catch (e) {
                      // no-op
                    }
                  }}
                >
                  Copy
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setBarcodeLogs([]);
                    try {
                      localStorage.removeItem(barcodeLogKey);
                    } catch (e) {
                      // no-op
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 1,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 180,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {(() => {
                try {
                  return JSON.stringify(barcodeLogs, null, 2);
                } catch (e) {
                  return '';
                }
              })()}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBarcodeLookupError('');
              setBarcodeLookupLoading(false);
              setBarcodeLookupResult(null);
              setBarcodeDialogPhase('scanning');
            }}
            color="primary"
          >
            Scan again
          </Button>

          {barcodeDialogPhase === 'found' && (
            <Button onClick={handleBarcodeUseResult} color="primary" variant="contained">
              Use result
            </Button>
          )}

          <Button onClick={handleBarcodeDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );

};

export default EditDialog;
